"use client"

import { Button } from "@/components/ui/button"
import Link from "next/link"

/**
 * Shared header component for error and not-found pages
 */
export const StatusPageHeader = ({
  title,
  description,
  icon: Icon,
  iconColor = "text-gray-600",
  iconBgColor = "bg-gray-100",
}: {
  title: string
  description: string
  icon: React.ElementType
  iconColor?: string
  iconBgColor?: string
}) => (
  <div className="flex flex-col items-center space-y-4">
    <div className={`rounded-full ${iconBgColor} p-3`}>
      <Icon className={`h-12 w-12 ${iconColor}`} />
    </div>
    <div className="text-center">
      <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
      <p className="text-gray-500 mt-2">{description}</p>
    </div>
  </div>
)

/**
 * Shared suggestions list component
 */
export const SuggestionsList = ({
  title,
  suggestions,
}: {
  title: string
  suggestions: string[]
}) => (
  <div className="bg-gray-50 rounded-lg p-4">
    <h2 className="text-sm font-medium text-gray-900">{title}</h2>
    <ul className="mt-2 text-sm text-gray-600 space-y-2">
      {suggestions.map((suggestion, index) => (
        <li key={index} className="flex items-center">
          <span className="h-1.5 w-1.5 rounded-full bg-gray-600 mr-2" />
          {suggestion}
        </li>
      ))}
    </ul>
  </div>
)

/**
 * Shared action buttons component
 */
export const ActionButtons = ({
  primaryAction,
  secondaryAction,
}: {
  primaryAction: {
    text: string
    href?: string
    onClick?: () => void
  }
  secondaryAction: {
    text: string
    href?: string
    onClick?: () => void
  }
}) => (
  <div className="flex justify-center space-x-4">
    <Button
      variant="outline"
      onClick={secondaryAction.onClick}
      className="w-32"
    >
      {secondaryAction.text}
    </Button>
    <Button asChild className="w-40">
      {secondaryAction.href ? (
        <Link href={secondaryAction.href}>{primaryAction.text}</Link>
      ) : (
        <button onClick={primaryAction.onClick}>{primaryAction.text}</button>
      )}
    </Button>
  </div>
)
